import React, { useEffect } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';

export const DocTech = () => {
  useEffect(() => {
    AOS.init({ duration: 1000 });
  }, []);

  return (
    <div id="doctech" style={{ background: '#f8fafc', padding: '60px 0' }}>
      <div className="container">

        {/* Section Title */}
        <div className="section-title text-center" data-aos="fade-up" style={{ marginBottom: 50 }}>
          <h2>Advanced Technologies Powering GyanBuddy KMS</h2>
          <p style={{ color: '#555', maxWidth: 620, margin: '12px auto 0', fontSize: 16, lineHeight: 1.7 }}>
            Built on next-generation AI architectures, GyanBuddy KMS goes beyond conventional
            document management to deliver truly intelligent knowledge operations.
          </p>
        </div>

        {/* ── Block 1: Neuromorphic Computing ── */}
        <div className="row" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', marginBottom: 70 }}>

          {/* Text – left */}
          <div className="col-xs-12 col-md-6" data-aos="fade-right">
            <div style={S.textBox}>
              <div style={S.badge}>
                <i className="fa fa-microchip" style={S.badgeIcon} />
                Core Technology
              </div>
              <h3 style={S.h3}>Neuromorphic Computing</h3>
              <p style={S.para}>
                GyanBuddy KMS is powered by <strong>Neuromorphic Computing</strong> — an AI architecture
                that mimics the human brain's neural structure to process information the way humans do:
                in parallel, efficiently, and contextually.
              </p>
              <p style={S.para}>
                Unlike traditional computing that processes tasks sequentially, neuromorphic chips handle
                massive knowledge workloads with significantly lower energy consumption, enabling
                real-time, context-aware responses at scale.
              </p>
              <div style={S.featureList}>
                {[
                  { icon: 'fa-bolt',      text: 'Up to 10× faster knowledge retrieval' },
                  { icon: 'fa-leaf',      text: 'Energy-efficient AI processing' },
                  { icon: 'fa-brain',     text: 'Context-aware, human-like understanding' },
                  { icon: 'fa-expand',    text: 'Scales with your organisation data' },
                ].map(f => (
                  <div key={f.text} style={S.featureItem}>
                    <i className={`fa ${f.icon}`} style={S.featureIcon} />
                    <span style={S.featureText}>{f.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Image – right */}
          <div className="col-xs-12 col-md-6" data-aos="fade-left">
            <div style={S.imgWrapper}>
              <img
                src="img/portfolio/cyberlock.jpg"
                alt="Neuromorphic Computing"
                style={S.img}
              />
              <div style={{ ...S.imgBadge, background: 'linear-gradient(135deg,#1e3a8a,#2563eb)' }}>
                <i className="fa fa-microchip" style={{ marginRight: 6 }} />
                Neuromorphic AI
              </div>
            </div>
          </div>
        </div>

        {/* ── Divider ── */}
        <div style={S.divider} />

        {/* ── Block 2: Handwritten Data Recognition ── */}
        <div className="row" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', marginTop: 60 }}>

          {/* Image – left */}
          <div className="col-xs-12 col-md-6" data-aos="fade-right">
            <div style={S.imgWrapper}>
              <img
                src="img/portfolio/glassescomputer.jpg"
                alt="Handwritten Data Recognition"
                style={S.img}
              />
              <div style={{ ...S.imgBadge, background: 'linear-gradient(135deg,#065f46,#059669)' }}>
                <i className="fa fa-language" style={{ marginRight: 6 }} />
                Support 250+ Languages
              </div>
            </div>
          </div>

          {/* Text – right */}
          <div className="col-xs-12 col-md-6" data-aos="fade-left">
            <div style={S.textBox}>
              <div style={{ ...S.badge, background: '#ecfdf5', color: '#065f46' }}>
                <i className="fa fa-pencil" style={{ ...S.badgeIcon, color: '#059669' }} />
                Handwriting Intelligence
              </div>
              <h3 style={S.h3}>Handwritten Data Recognition</h3>
              <p style={S.para}>
                GyanBuddy KMS bridges the gap between physical and digital knowledge. Simply scan or
                upload any handwritten document — notes, forms, registers, or manuscripts — and our AI
                intelligently extracts, indexes, and makes the content fully searchable.
              </p>
              <p style={S.para}>
                With support on <strong>250+ languages</strong> for OCR, GyanBuddy KMS
                ensures no knowledge is left behind regardless of the language or writing style.
              </p>
              <div style={S.featureList}>
                {[
                  { icon: 'fa-globe',       text: '250+ languages including regional scripts' },
                  { icon: 'fa-search',      text: 'Full-text search on handwritten content' },
                  { icon: 'fa-upload',      text: 'Scan, upload, and process in seconds' },
                  { icon: 'fa-file-text-o', text: 'Auto-index alongside your digital documents' },
                ].map(f => (
                  <div key={f.text} style={S.featureItem}>
                    <i className={`fa ${f.icon}`} style={{ ...S.featureIcon, color: '#059669' }} />
                    <span style={S.featureText}>{f.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

const S = {
  textBox: {
    padding: '10px 0 10px 10px',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: '#eff6ff',
    color: '#1e40af',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    padding: '5px 12px',
    borderRadius: 20,
    marginBottom: 14,
  },
  badgeIcon: {
    fontSize: 13,
    color: '#2563eb',
  },
  h3: {
    fontSize: 'clamp(22px,3vw,30px)',
    fontWeight: 800,
    color: '#0f172a',
    margin: '0 0 16px',
    lineHeight: 1.25,
  },
  para: {
    color: '#475569',
    fontSize: 15,
    lineHeight: 1.75,
    margin: '0 0 14px',
  },
  featureList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    marginTop: 8,
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  featureIcon: {
    fontSize: 15,
    color: '#2563eb',
    width: 20,
    textAlign: 'center',
    flexShrink: 0,
  },
  featureText: {
    color: '#334155',
    fontSize: 14,
    fontWeight: 500,
  },
  imgWrapper: {
    position: 'relative',
    borderRadius: 18,
    overflow: 'hidden',
    boxShadow: '0 8px 40px rgba(0,0,0,0.14)',
  },
  img: {
    width: '100%',
    height: 340,
    objectFit: 'cover',
    display: 'block',
  },
  imgBadge: {
    position: 'absolute',
    bottom: 16,
    left: 16,
    color: '#fff',
    fontSize: 13,
    fontWeight: 700,
    padding: '6px 14px',
    borderRadius: 20,
    display: 'flex',
    alignItems: 'center',
  },
  divider: {
    height: 1,
    background: 'linear-gradient(90deg,transparent,#cbd5e1,transparent)',
    margin: '0 40px',
  },
};
